import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles, Brain, Compass, HeartPulse, GraduationCap, Crown, Wallet, Users,
  Plus, Image as ImageIcon, Video, Link2, Mic, Pin, Trash2, Edit3, Wand2,
  Target, Flame, Trophy, Star, ChevronRight, ChevronDown, Loader2, Save,
  Briefcase, Award, MapPin, BookOpen, Rocket, Heart, Globe, ArrowRight,
  CheckCircle2, Circle, Bot, X, Send, Download, Settings as SettingsIcon, CalendarPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import PageHead from "@/components/PageHead";
import { supabase } from "@/integrations/supabase/client";
import { useSkillsIntelligence } from "@/hooks/useSkillsIntelligence";
import { FutureProbabilityPanel } from "@/components/skills/FutureProbabilityPanel";
import { useAuth } from "@/contexts/AuthContext";
import { computeYomaNudges } from "@/lib/visionBoardNudges";
import { archivePurposeVersion, restorePurposeVersion, MAX_PURPOSE_VERSIONS } from "@/lib/purposeVersions";
import { collectMediaPaths, verifyMediaResolved } from "@/lib/visionBoardMedia";
import { describeUploadError } from "@/lib/uploadErrors";
import { diffWords } from "@/lib/textDiff";
import { History, RotateCcw, AlertTriangle, Pencil } from "lucide-react";
import { cloudStorage } from "@/lib/cloudStore";
import {
  type DailyBook, type TaskSchedule, type HistoryFilter, WEEKDAY_LABELS,
  normaliseBook, computeStreak, tasksForDate, logFor, isDayComplete, historyEntries,
  toggleTask, setNote as setNoteFor, addTask, updateTask, archiveTask, restoreTask,
  describeSchedule, formatDay, shiftDay, todayKey,
  EMPTY_HISTORY_FILTER, filterHistory, exportPayload, exportFilename, parseImport, mergeBooks,
} from "@/lib/dailyDiary";
import {
  type ReminderSettings, DEFAULT_REMINDER, fetchReminderSettings, saveReminderSettings,
  formatHour, guessTimezone,
} from "@/lib/dailyReminders";
import DiaryHeatmap from "@/components/vision/DiaryHeatmap";
import { googleCalendarUrl } from "@/lib/googleCalendarLink";



import {
  loadYomaThread, saveYomaThread, streamYomaReply, type YomaTurn,
} from "@/lib/yomaChat";

import RoleTitleInput from "@/components/career/RoleTitleInput";
import { useCareerDna } from "@/hooks/useCareerDna";

// =========================
// Types & local storage
// =========================
type Dream = {
  id: string;
  title: string;
  note?: string;
  imageUrl?: string;
  imagePath?: string;
  videoUrl?: string;
  videoPath?: string;
  link?: string;
  voiceNote?: string;
  voicePath?: string;
  targetYear?: number;
  priority?: "low" | "medium" | "high";
  tags?: string[];
  createdAt: number;
};

type WheelArea = {
  key: string;
  label: string;
  current: number;
  target: number;
  color: string;
};

type GalleryItem = {
  id: string;
  type: "image" | "quote" | "video" | "link";
  src?: string;
  path?: string;
  text?: string;
  tag?: string;
  priority?: "low" | "medium" | "high";
  targetYear?: number;
};

type PurposePack = {
  purpose: string;
  mission: string;
  vision: string;
  values: string[];
  motto: string;
  brand: string;
  leadership: string;
  futureIdentity: string;
  /** Auto-archived prior versions so regenerate/edit never loses prior work. */
  _versions?: Array<{ savedAt: number; pack: Omit<PurposePack, "_versions"> }>;
};

type ActionStep = { id: string; label: string; xp: number; difficulty: 1|2|3; hours: number; done: boolean };
type ActionPlan = { dreamId: string; dreamTitle: string; steps: ActionStep[] };
type ArchivedPlan = ActionPlan & { archivedAt: number; reason: "dream_changed" | "regenerated"; version: number };

/** Upload a media file for the current user to the private `vision-board` bucket
 *  under `<user_id>/<uuid>.<ext>`. Returns the storage path + a long-lived signed URL. */
export async function uploadVisionMedia(userId: string, file: File): Promise<{ path: string; url: string }> {
  const ext = (file.name.split(".").pop() || "bin").toLowerCase();
  const path = `${userId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from("vision-board").upload(path, file, {
    cacheControl: "31536000",
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw error;
  const { data, error: sErr } = await supabase.storage.from("vision-board").createSignedUrl(path, 60 * 60 * 24 * 365);
  if (sErr || !data?.signedUrl) throw sErr ?? new Error("signed_url_failed");
  return { path, url: data.signedUrl };
}

/** Refresh signed URLs for media paths after page reload / session switch. */
export async function resolveVisionMedia(paths: string[]): Promise<Record<string, string>> {
  if (!paths.length) return {};
  const { data, error } = await supabase.storage.from("vision-board").createSignedUrls(paths, 60 * 60 * 24 * 365);
  if (error || !data) return {};
  const out: Record<string, string> = {};
  for (const row of data) if (row.path && row.signedUrl) out[row.path] = row.signedUrl;
  return out;
}

const LS = {
  dreams: "wekit:vb:dreams:v1",
  wheel: "wekit:vb:wheel:v1",
  purpose: "wekit:vb:purpose:v1",
  gallery: "wekit:vb:gallery:v1",
  actions: "wekit:vb:actions:v1",
  actionArchives: "wekit:vb:actionArchives:v1",
  daily: "wekit:vb:daily:v1",
  streak: "wekit:vb:streak:v1",
  timeline: "wekit:vb:timeline:v1",
} as const;

const load = <T,>(key: string, fallback: T): T => {
  try {
    const raw = cloudStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};
const save = <T,>(key: string, val: T) => {
  try { cloudStorage.setItem(key, JSON.stringify(val)); } catch { /* ignore */ }
};
const uid = () => Math.random().toString(36).slice(2, 10);

// =========================
// Defaults
// =========================
const DEFAULT_WHEEL: WheelArea[] = [
  { key: "career", label: "Career", current: 5, target: 9, color: "#00665B" },
  { key: "health", label: "Health", current: 6, target: 9, color: "#FF5F1F" },
  { key: "learning", label: "Learning", current: 7, target: 10, color: "#0205F4" },
  { key: "finance", label: "Finance", current: 4, target: 8, color: "#0818A8" },
  { key: "relationships", label: "Relationships", current: 6, target: 9, color: "#e94560" },
  { key: "purpose", label: "Purpose", current: 5, target: 10, color: "#9b5cff" },
  { key: "adventure", label: "Adventure", current: 4, target: 8, color: "#22d3ee" },
  { key: "giving", label: "Giving Back", current: 3, target: 8, color: "#f59e0b" },
];

const CONSTELLATION_NODES = [
  { key: "career", label: "Career", x: 50, y: 50 },
  { key: "skills", label: "Skills", x: 78, y: 28 },
  { key: "purpose", label: "Purpose", x: 24, y: 24 },
  { key: "health", label: "Health", x: 86, y: 64 },
  { key: "learning", label: "Learning", x: 18, y: 62 },
  { key: "leadership", label: "Leadership", x: 50, y: 12 },
  { key: "finance", label: "Finance", x: 72, y: 86 },
  { key: "relationships", label: "Relationships", x: 28, y: 88 },
];

const SUGGESTED_DREAMS = [
  "AI Engineer", "Entrepreneur", "Doctor", "Wildlife Photographer",
  "Pilot", "Game Designer", "Musician", "Lawyer", "Scientist", "Architect",
];

// =========================
// Helpers
// =========================


function useAssessmentScores() {
  const [scores, setScores] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    try {
      const raw = cloudStorage.getItem("wekit_trait_scores");
      if (raw) setScores(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);
  return scores;
}

function deriveIdentity(scores: Record<string, number> | null) {
  const get = (k: string) => (scores?.[k] ?? 0);
  const top = scores
    ? Object.entries(scores).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([k])=>k)
    : ["Curiosity","Empathy","Discipline","Creativity","Leadership"];
  const archetype = top[0]?.includes("Lead") ? "The Visionary Leader"
    : top[0]?.includes("Creat") ? "The Creative Builder"
    : top[0]?.includes("Empath") ? "The Empathic Connector"
    : top[0]?.includes("Analy") ? "The Strategic Analyst"
    : "The Curious Explorer";
  const learning = get("Curiosity") > 6 ? "Exploratory & hands-on" : "Structured & deep";
  const workStyle = get("Discipline") > 6 ? "Focused, deadline-driven" : "Flexible & collaborative";
  const motivators = top.slice(0,3);
  const confidence = scores
    ? Math.min(99, 60 + Math.round(Object.values(scores).reduce((s,v)=>s+v,0) / Math.max(1,Object.keys(scores).length) * 4))
    : 78;
  return { archetype, strengths: top.slice(0,4), learning, workStyle, values: top.slice(1,5), motivators, confidence };
}

// =========================
// Main page
// =========================
export default function VisionBoard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const scores = useAssessmentScores();
  const identity = useMemo(() => deriveIdentity(scores), [scores]);

  const [dreams, setDreams] = useState<Dream[]>(() => load<Dream[]>(LS.dreams, []));
  const [wheel, setWheel] = useState<WheelArea[]>(() => load<WheelArea[]>(LS.wheel, DEFAULT_WHEEL));
  const [purpose, setPurpose] = useState<PurposePack | null>(() => load<PurposePack | null>(LS.purpose, null));
  const [gallery, setGallery] = useState<GalleryItem[]>(() => load<GalleryItem[]>(LS.gallery, []));
  const [actions, setActions] = useState<ActionPlan[]>(() => load<ActionPlan[]>(LS.actions, []));
  const [actionArchives, setActionArchives] = useState<ArchivedPlan[]>(() => load<ArchivedPlan[]>(LS.actionArchives, []));
  const [book, setBook] = useState<DailyBook>(() => normaliseBook(load<unknown>(LS.daily, null)));
  const streak = useMemo(() => computeStreak(book), [book]);

  // Persist locally (instant) + cloud (durable, cross-session)
  useEffect(() => save(LS.dreams, dreams), [dreams]);
  useEffect(() => save(LS.wheel, wheel), [wheel]);
  useEffect(() => save(LS.purpose, purpose), [purpose]);
  useEffect(() => save(LS.gallery, gallery), [gallery]);
  useEffect(() => save(LS.actions, actions), [actions]);
  useEffect(() => save(LS.actionArchives, actionArchives), [actionArchives]);
  useEffect(() => save(LS.daily, book), [book]);
  useEffect(() => save(LS.streak, streak), [streak]);


  // Cloud: hydrate from DB on login (overrides local if cloud is newer / non-empty)
  const [cloudLoaded, setCloudLoaded] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [syncing, setSyncing] = useState(false);
  useEffect(() => {
    if (!user?.id) { setCloudLoaded(true); return; }
    let active = true;
    (async () => {
      const { data } = await supabase
        .from("vision_boards")
        .select("dreams,wheel,purpose,gallery,actions,daily,streak,updated_at")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!active) return;
      if (data) {
        // Re-mint signed URLs for any media saved as storage paths so they
        // load correctly across new sessions even after the prior URL expired.
        const dreamRows = Array.isArray(data.dreams) ? (data.dreams as Dream[]) : [];
        const galleryRows = Array.isArray(data.gallery) ? (data.gallery as GalleryItem[]) : [];
        const paths = [
          ...dreamRows.flatMap((d) => [d.imagePath, d.videoPath, d.voicePath]).filter(Boolean) as string[],
          ...galleryRows.map((g) => g.path).filter(Boolean) as string[],
        ];
        const resolved = paths.length ? await resolveVisionMedia(paths) : {};
        // Verify every persisted media path was re-minted to a usable signed
        // URL — surfaces a clear warning instead of silently broken media
        // when a refresh / new session can't reload prior uploads.
        const verification = verifyMediaResolved(
          collectMediaPaths(dreamRows, galleryRows),
          resolved,
        );
        if (!verification.ok) {
          console.warn("[vision-board] media verification failed", verification.missing);
          toast.warning(
            `${verification.missing.length} of ${verification.total} Vision Board media items couldn't reload — try again in a moment.`,
          );
        }
        if (dreamRows.length) {
          setDreams(dreamRows.map((d) => ({
            ...d,
            imageUrl: d.imagePath ? (resolved[d.imagePath] ?? d.imageUrl) : d.imageUrl,
            videoUrl: d.videoPath ? (resolved[d.videoPath] ?? d.videoUrl) : d.videoUrl,
            voiceNote: d.voicePath ? (resolved[d.voicePath] ?? d.voiceNote) : d.voiceNote,
          })));
        }
        if (Array.isArray(data.wheel) && data.wheel.length) setWheel(data.wheel as WheelArea[]);
        if (data.purpose) setPurpose(data.purpose as PurposePack);
        if (galleryRows.length) {
          setGallery(galleryRows.map((g) => ({
            ...g,
            src: g.path ? (resolved[g.path] ?? g.src) : g.src,
          })));
        }
        if (Array.isArray(data.actions) && data.actions.length) setActions(data.actions as ActionPlan[]);
        if (data.daily) setBook(normaliseBook(data.daily));

        if (data.updated_at) setLastSyncedAt(new Date(data.updated_at).getTime());
      }
      setCloudLoaded(true);
    })();
    return () => { active = false; };
  }, [user?.id]); // eslint-disable-line

  // Debounced cloud upsert
  useEffect(() => {
    if (!user?.id || !cloudLoaded) return;
    setSyncing(true);
    const t = setTimeout(() => {
      supabase.from("vision_boards").upsert({
        user_id: user.id,
        dreams, wheel, purpose, gallery, actions,
        daily: JSON.parse(JSON.stringify(book)), streak,

        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id" }).then(({ error }) => {
        setSyncing(false);
        if (error) {
          console.warn("vision_board sync failed", error.message);
        } else {
          setLastSyncedAt(Date.now());
        }
      });
    }, 800);
    return () => clearTimeout(t);
  }, [user?.id, cloudLoaded, dreams, wheel, purpose, gallery, actions, book, streak]);




  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageHead
        title="Future Command Center · WeKIT™ Career Clarity 360"
        description="Turn your dreams into an AI-powered roadmap. Your living life-design system."
        path="/vision-board"
        noIndex
      />

      <BackgroundFX />

      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/60 border-b border-white/5">
        <div className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex min-h-11 items-center font-display text-sm text-muted-foreground hover:text-foreground">← Dashboard</Link>
          <div className="font-display text-xs sm:text-sm tracking-wide text-muted-foreground">
            WeKIT™ · <span className="text-foreground">Future Command Center</span>
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-3">
            <span data-testid="vb-sync-status" title={lastSyncedAt ? `Last synced ${new Date(lastSyncedAt).toLocaleTimeString()}` : "Sync pending"}
              className="hidden sm:inline-flex items-center gap-1.5">
              {syncing ? (
                <><Loader2 className="h-3 w-3 animate-spin" /> Syncing…</>
              ) : lastSyncedAt ? (
                <><CheckCircle2 className="h-3 w-3 text-primary" /> Synced</>
              ) : user?.id ? (
                <><Loader2 className="h-3 w-3 animate-spin" /> Loading…</>
              ) : (
                <span className="text-amber-400">Sign in to sync</span>
              )}
            </span>
            <span className="hidden sm:inline-flex items-center gap-1">
              <Flame className="h-3.5 w-3.5 text-orange-400" /> {streak.count} day streak
            </span>
          </div>
        </div>
      </header>

      <Hero identity={identity} dreamsCount={dreams.length} />

      <SectionWhoIAm identity={identity} />

      <SectionDreams dreams={dreams} setDreams={setDreams} actions={actions} setActions={setActions} archives={actionArchives} setArchives={setActionArchives} userId={user?.id ?? null} />


      <SectionLifeWheel wheel={wheel} setWheel={setWheel} />

      <SectionPurpose purpose={purpose} setPurpose={setPurpose} identity={identity} />

      <SectionTimeline />

      <SectionActions actions={actions} setActions={setActions} archives={actionArchives} setArchives={setActionArchives} />

      <SectionOpportunities identity={identity} dreams={dreams} navigate={navigate} />

      <SectionGallery gallery={gallery} setGallery={setGallery} userId={user?.id ?? null} />

      <SectionDaily
        book={book} setBook={setBook}
        streak={streak}
        userId={user?.id ?? null}
      />


      <SectionProbability identity={identity} actions={actions} wheel={wheel} dreams={dreams} streak={streak} />

      <SectionNorthStar purpose={purpose} streak={streak} actions={actions} />

      <FloatingYoma identity={identity} dreams={dreams} actions={actions} wheel={wheel} streak={streak} />


      <footer className="container mx-auto px-4 sm:px-6 py-12 text-center text-xs text-muted-foreground">
        Built with ♥ for those who refuse to wait for their future. · WeKIT™ Career Clarity 360
      </footer>
    </div>
  );
}

// =========================
// Background FX
// =========================
function BackgroundFX() {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className="absolute -top-40 -left-40 w-[40rem] h-[40rem] rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(circle, #00665B 0%, transparent 60%)" }} />
      <div className="absolute top-1/3 -right-40 w-[34rem] h-[34rem] rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(circle, #0205F4 0%, transparent 60%)" }} />
      <div className="absolute bottom-0 left-1/4 w-[36rem] h-[36rem] rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #FF5F1F 0%, transparent 60%)" }} />
    </div>
  );
}

// =========================
// Glass section wrapper
// =========================
function Section({ id, eyebrow, title, subtitle, children }: {
  id?: string; eyebrow?: string; title: string; subtitle?: string; children: React.ReactNode;
}) {
  return (
    <section id={id} className="container mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="mb-8 sm:mb-12"
      >
        {eyebrow && (
          <div className="text-[11px] font-display uppercase tracking-[0.3em] text-primary/80 mb-3">{eyebrow}</div>
        )}
        <h2 className="font-display text-3xl sm:text-5xl font-bold text-balance">{title}</h2>
        {subtitle && <p className="mt-3 text-muted-foreground max-w-2xl">{subtitle}</p>}
      </motion.div>
      {children}
    </section>
  );
}

function GlassCard({ children, className = "", onClick }: { children: React.ReactNode; className?: string; onClick?: () => void }) {
  return (
    <div onClick={onClick} className={`relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl shadow-2xl shadow-black/30 ${className}`}>
      {children}
    </div>
  );
}

// =========================
// HERO + Constellation
// =========================
function Hero({ identity, dreamsCount }: { identity: ReturnType<typeof deriveIdentity>; dreamsCount: number }) {
  const litCount = Math.min(CONSTELLATION_NODES.length, 3 + Math.floor(dreamsCount / 2) + Math.floor((identity.confidence - 60) / 10));

  return (
    <section className="container mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-12">
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
          <div className="text-[11px] font-display uppercase tracking-[0.3em] text-primary/80 mb-4">
            Future Command Center
          </div>
          <h1 className="font-display text-5xl sm:text-7xl font-bold leading-[1.02] text-balance">
            My Future <span className="bg-clip-text text-transparent bg-gradient-to-r from-[hsl(168,80%,55%)] via-[hsl(228,80%,65%)] to-[hsl(20,95%,60%)]">Starts Today.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
            This is your AI-powered Future Command Center. Everything you dream, build and achieve begins here.
          </p>
          <p className="mt-3 text-sm text-primary/80 font-display">
            Turn your dreams into an AI-powered roadmap.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" className="rounded-full" onClick={() => document.getElementById("dreams")?.scrollIntoView({ behavior: "smooth" })}>
              <Sparkles className="h-4 w-4" /> Add a dream
            </Button>
            <Button size="lg" variant="outline" className="rounded-full" onClick={() => document.getElementById("purpose")?.scrollIntoView({ behavior: "smooth" })}>
              <Wand2 className="h-4 w-4" /> Build my purpose
            </Button>
          </div>
        </motion.div>

        <Constellation litCount={litCount} />
      </div>
    </section>
  );
}

function Constellation({ litCount }: { litCount: number }) {
  return (
    <div className="relative aspect-square w-full max-w-[520px] mx-auto">
      <div className="absolute inset-0 rounded-full opacity-30 blur-2xl"
        style={{ background: "radial-gradient(circle, rgba(0,102,91,0.7), transparent 60%)" }} />
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
        {CONSTELLATION_NODES.map((n, i) =>
          CONSTELLATION_NODES.slice(i+1).map((m, j) => (
            <line
              key={`${i}-${j}`}
              x1={n.x} y1={n.y} x2={m.x} y2={m.y}
              stroke="rgba(255,255,255,0.08)"
              strokeWidth={0.2}
            />
          ))
        )}
        {CONSTELLATION_NODES.map((n, i) => {
          const lit = i < litCount;
          return (
            <g key={n.key}>
              {lit && (
                <circle cx={n.x} cy={n.y} r={3.5} fill="rgba(0,200,180,0.18)">
                  <animate attributeName="r" values="3;5;3" dur="3s" repeatCount="indefinite" />
                </circle>
              )}
              <circle
                cx={n.x} cy={n.y} r={1.5}
                fill={lit ? "#5eead4" : "rgba(255,255,255,0.25)"}
              />
              <text x={n.x} y={n.y - 3.5} textAnchor="middle" fontSize="2.6"
                fill={lit ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.4)"}
                style={{ fontFamily: "Space Grotesk" }}>
                {n.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// =========================
// SECTION 1 — Who I Am
// =========================
function SectionWhoIAm({ identity }: { identity: ReturnType<typeof deriveIdentity> }) {
  const cards = [
    { icon: Crown, label: "Personality Archetype", value: identity.archetype },
    { icon: Star, label: "Top Strengths", value: identity.strengths.join(" · ") },
    { icon: BookOpen, label: "Learning Style", value: identity.learning },
    { icon: Briefcase, label: "Work Style", value: identity.workStyle },
    { icon: Heart, label: "Core Values", value: identity.values.join(" · ") },
    { icon: Rocket, label: "Natural Motivators", value: identity.motivators.join(" · ") },
    { icon: Brain, label: "Career Personality", value: identity.archetype.replace("The ", "") },
    { icon: Compass, label: "Top Interests", value: identity.strengths.slice(0,3).join(" · ") },
  ];

  return (
    <Section eyebrow="Section 01" title="Who I Am" subtitle="Drawn directly from your WeKIT™ Career Clarity 360 assessment.">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <motion.div key={c.label}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.05 }}>
            <GlassCard className="p-5 h-full hover:border-primary/30 transition">
              <div className="flex items-center justify-between">
                <c.icon className="h-5 w-5 text-primary" />
                <Badge variant="outline" className="text-[10px]">Validated</Badge>
              </div>
              <div className="mt-4 text-[11px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
              <div className="mt-1 font-display text-base font-semibold leading-snug line-clamp-2">{c.value}</div>
            </GlassCard>
          </motion.div>
        ))}
      </div>
      <div className="mt-6 flex items-center gap-3">
        <div className="text-xs text-muted-foreground">Identity confidence</div>
        <Progress value={identity.confidence} className="h-2 max-w-xs" />
        <div className="text-xs font-display">{identity.confidence}%</div>
      </div>
    </Section>
  );
}

// =========================
// SECTION 2 — Dreams
// =========================
function SectionDreams({ dreams, setDreams, actions, setActions, archives, setArchives, userId }: {
  dreams: Dream[]; setDreams: (d: Dream[]) => void;
  actions: ActionPlan[]; setActions: (a: ActionPlan[]) => void;
  archives: ArchivedPlan[]; setArchives: (a: ArchivedPlan[]) => void;
  userId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Dream | null>(null);
  // The board is only honest if it reflects the run the learner just finished:
  // these come from the same Career DNA™ snapshot the result screen wrote.
  const { data: dna } = useCareerDna();
  const pinnedTitles = new Set(dreams.map((d) => d.title.trim().toLowerCase()));
  const fromResults = (dna?.matches ?? [])
    .filter((m) => m.name && !pinnedTitles.has(m.name.trim().toLowerCase()))
    .slice(0, 4);
  const [draft, setDraft] = useState<Dream>({
    id: "", title: "", note: "", priority: "medium", targetYear: new Date().getFullYear() + 3, createdAt: Date.now(),
  });

  const openNew = (title = "") => {
    setEditing(null);
    setDraft({ id: uid(), title, note: "", priority: "medium", targetYear: new Date().getFullYear() + 3, createdAt: Date.now() });
    setOpen(true);
  };
  const openEdit = (d: Dream) => { setEditing(d); setDraft(d); setOpen(true); };

  const [uploading, setUploading] = useState<"image" | "video" | "voice" | null>(null);
  const [uploadError, setUploadError] = useState<{ kind: "image" | "video" | "voice"; message: string; retryable: boolean } | null>(null);

  /** Upload to private storage when signed in (durable across sessions), else
   *  fall back to a base64 data URL so the dream still persists locally. */
  const attachFile = async (file: File, kind: "image" | "video" | "voice") => {
    setUploading(kind);
    setUploadError(null);
    try {
      if (userId) {
        const { path, url } = await uploadVisionMedia(userId, file);
        const next = { ...draft } as Dream;
        if (kind === "image") { next.imagePath = path; next.imageUrl = url; }
        if (kind === "video") { next.videoPath = path; next.videoUrl = url; }
        if (kind === "voice") { next.voicePath = path; next.voiceNote = url; }
        setDraft(next);
        toast.success(`${kind.charAt(0).toUpperCase() + kind.slice(1)} attached & synced`);
      } else {
        const reader = new FileReader();
        const url = await new Promise<string>((res, rej) => {
          reader.onload = () => res(reader.result as string);
          reader.onerror = () => rej(reader.error);
          reader.readAsDataURL(file);
        });
        const next = { ...draft } as Dream;
        if (kind === "image") next.imageUrl = url;
        if (kind === "video") next.videoUrl = url;
        if (kind === "voice") next.voiceNote = url;
        setDraft(next);
      }
    } catch (e) {
      console.error("attachFile", e);
      const info = describeUploadError(e, { size: file.size, type: file.type });
      setUploadError({ kind, message: info.message, retryable: info.retryable });
      toast.error(info.title, {
        description: info.message,
        action: info.retryable
          ? { label: "Retry", onClick: () => { void attachFile(file, kind); } }
          : undefined,
      });
    } finally {
      setUploading(null);
    }
  };



  const nextVersion = (dreamId: string) =>
    (archives.filter(a => a.dreamId === dreamId).reduce((m, a) => Math.max(m, a.version), 0)) + 1;

  /** Move a current plan into archives, with an Undo toast that restores it. */
  const archiveCurrent = (plan: ActionPlan, reason: "dream_changed" | "regenerated") => {
    const archived: ArchivedPlan = { ...plan, archivedAt: Date.now(), reason, version: nextVersion(plan.dreamId) };
    setArchives([archived, ...archives]);
    const label = reason === "dream_changed" ? "Dream changed" : "Fresh roadmap generated";
    toast.success(`${label} — “Path to ${plan.dreamTitle}” v${archived.version} archived`, {
      description: `${plan.steps.filter(s => s.done).length}/${plan.steps.length} steps were complete.`,
      action: {
        label: "Undo",
        onClick: () => {
          // Pull the just-archived entry out and reinstate it as the current plan.
          setArchives(archives);
          setActions([plan, ...actions.filter(a => a.dreamId !== plan.dreamId)]);
          toast.info("Archive undone — roadmap restored");
        },
      },
    });
    return archived;
  };

  const saveDream = () => {
    if (!draft.title.trim()) { toast.error("Give your dream a name"); return; }
    if (editing) {
      const titleChanged = editing.title.trim() !== draft.title.trim();
      setDreams(dreams.map(d => d.id === editing.id ? draft : d));
      if (titleChanged) {
        const prior = actions.find(a => a.dreamId === editing.id);
        if (prior) {
          archiveCurrent(prior, "dream_changed");
          setActions(actions.filter(a => a.dreamId !== editing.id));
        } else {
          toast.success("Dream updated");
        }
      } else {
        toast.success("Dream updated");
      }
    } else {
      setDreams([draft, ...dreams]);
      toast.success("Added to your Vision Board.");
    }
    setOpen(false);
  };

  const removeDream = (id: string) => {
    setDreams(dreams.filter(d => d.id !== id));
    const prior = actions.find(a => a.dreamId === id);
    if (prior) archiveCurrent(prior, "dream_changed");
    setActions(actions.filter(a => a.dreamId !== id));
  };

  const buildPlan = (dream: Dream): ActionPlan => {
    const stepLabels = [
      `Research the path to ${dream.title}`,
      `Identify 3 must-have skills for ${dream.title}`,
      `Build a 30-day learning sprint`,
      `Ship a first project portfolio piece`,
      `Find a mentor or community in ${dream.title}`,
      `Apply for an internship / opportunity`,
      `Earn a recognized certification`,
      `Land first real-world experience`,
    ];
    return {
      dreamId: dream.id,
      dreamTitle: dream.title,
      steps: stepLabels.map((label, i) => ({
        id: uid(), label, xp: 50 + i * 25,
        difficulty: ((i % 3) + 1) as 1|2|3,
        hours: 5 + i * 3, done: false,
      })),
    };
  };

  const generateActionPlan = (dream: Dream) => {
    const exists = actions.find(a => a.dreamId === dream.id);
    const plan = buildPlan(dream);
    if (exists) {
      archiveCurrent(exists, "regenerated");
      setActions([plan, ...actions.filter(a => a.dreamId !== dream.id)]);
    } else {
      setActions([plan, ...actions]);
      toast.success(`New roadmap for “${dream.title}” created`);
    }
    document.getElementById("actions")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Section id="dreams" eyebrow="Section 02" title="Who I Want To Become"
      subtitle="Pin the futures that move you. Add images, videos, voice notes — anything that keeps the vision alive.">
      {fromResults.length > 0 && (
        <div className="mb-6 rounded-2xl border border-primary/25 bg-primary/[0.06] p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
            <span className="text-xs font-medium uppercase tracking-[0.14em] text-primary">
              From your latest results
            </span>
            {dna?.localSavedAt && (
              <span className="text-[11px] text-muted-foreground">
                saved {new Date(dna.localSavedAt).toLocaleDateString()}
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {dna?.band === "12to14"
              ? "Kinds of work that matched what you told us. Pin one to keep an eye on it."
              : "Careers that matched your answers. Pin the ones you want to aim at."}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {fromResults.map((m) => (
              <button
                key={m.id || m.name}
                onClick={() => openNew(m.name)}
                aria-label={`Pin ${m.name} to your vision board`}
                className="min-h-11 px-4 py-2 rounded-full text-xs border border-primary/30 bg-primary/10
                  hover:bg-primary/20 transition inline-flex items-center gap-1
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <Plus className="h-3 w-3" aria-hidden="true" /> {m.name}
              </button>
            ))}
            <Link
              to="/careers"
              className="min-h-11 px-4 py-2 rounded-full text-xs border border-white/10 bg-white/[0.03]
                hover:bg-white/[0.06] transition inline-flex items-center gap-1
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              See all matches <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </Link>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6">
        {SUGGESTED_DREAMS.map(s => (
          <button key={s} onClick={() => openNew(s)}
            className="min-h-11 px-4 py-2 rounded-full text-xs border border-white/10 bg-white/[0.03] hover:bg-primary/10 hover:border-primary/30 transition">
            + {s}
          </button>
        ))}
        <button onClick={() => openNew()}
          className="min-h-11 px-4 py-2 rounded-full text-xs bg-primary text-primary-foreground font-medium inline-flex items-center gap-1">
          <Plus className="h-3 w-3" /> Custom dream
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnimatePresence>
          {dreams.map((d, i) => (
            <motion.div key={d.id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.35, delay: i * 0.04 }}>
              <GlassCard className="overflow-hidden group hover:border-primary/40 transition">
                {d.imageUrl && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={d.imageUrl} alt={d.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                  </div>
                )}
                {d.videoUrl && (
                  <video src={d.videoUrl} controls className="w-full max-h-56 bg-black" />
                )}
                <div className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-lg font-semibold">{d.title}</div>
                      {d.targetYear && <div className="text-[11px] text-muted-foreground">by {d.targetYear}</div>}
                    </div>
                    <Badge variant="outline" className="text-[10px] capitalize">{d.priority ?? "medium"}</Badge>
                  </div>
                  {d.note && <p className="text-sm text-muted-foreground line-clamp-3">{d.note}</p>}
                  {d.voiceNote && <audio src={d.voiceNote} controls className="w-full" />}
                  {d.link && (
                    <a href={d.link} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 hover:underline">
                      <Link2 className="h-3 w-3" /> Inspiration link
                    </a>
                  )}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button size="sm" variant="default" className="rounded-full text-xs min-h-11 px-4" onClick={() => generateActionPlan(d)}>
                      <Wand2 className="h-3 w-3" /> Generate plan
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-full text-xs min-h-11 min-w-11" aria-label={`Edit dream: ${d.title}`} onClick={() => openEdit(d)}>
                      <Edit3 className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-full text-xs min-h-11 min-w-11 text-destructive" aria-label={`Delete dream: ${d.title}`} onClick={() => removeDream(d.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </AnimatePresence>

        {dreams.length === 0 && (
          <GlassCard className="p-10 text-center sm:col-span-2 lg:col-span-3">
            <Sparkles className="h-8 w-8 mx-auto text-primary mb-3" />
            <div className="font-display text-lg">Pick your first dream above</div>
            <div className="text-sm text-muted-foreground mt-1">Every great future starts with a single yes.</div>
          </GlassCard>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit dream" : "Pin a new dream"}</DialogTitle>
            <DialogDescription>Add images, videos, links or voice notes to bring it alive.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <RoleTitleInput
              value={draft.title}
              onChange={title => setDraft({ ...draft, title })}
              placeholder="Dream role or title (start typing…)"
              aria-label="Dream role or title"
            />
            <Textarea placeholder="Why this matters to you..." value={draft.note ?? ""} onChange={e => setDraft({ ...draft, note: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" placeholder="Target year" value={draft.targetYear ?? ""} onChange={e => setDraft({ ...draft, targetYear: Number(e.target.value) })} />
              <select className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={draft.priority ?? "medium"}
                onChange={e => setDraft({ ...draft, priority: e.target.value as Dream["priority"] })}>
                <option value="low">Low priority</option>
                <option value="medium">Medium priority</option>
                <option value="high">High priority</option>
              </select>
            </div>
            <Input placeholder="Inspiration URL (YouTube, article, Insta...)" value={draft.link ?? ""} onChange={e => setDraft({ ...draft, link: e.target.value })} />
            <div className="grid grid-cols-3 gap-2">
              <label className={`flex flex-col items-center justify-center gap-1 text-xs rounded-md border border-input bg-background px-2 py-3 cursor-pointer hover:border-primary/40 transition ${uploading === "image" ? "opacity-60" : ""}`}>
                {uploading === "image" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                <span>Image</span>
                <input type="file" accept="image/*" className="hidden" disabled={uploading !== null} onChange={async e => {
                  const f = e.target.files?.[0]; if (!f) return;
                  await attachFile(f, "image");
                  e.currentTarget.value = "";
                }} />
              </label>
              <label className={`flex flex-col items-center justify-center gap-1 text-xs rounded-md border border-input bg-background px-2 py-3 cursor-pointer hover:border-primary/40 transition ${uploading === "video" ? "opacity-60" : ""}`}>
                {uploading === "video" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />}
                <span>Video</span>
                <input type="file" accept="video/*" className="hidden" disabled={uploading !== null} onChange={async e => {
                  const f = e.target.files?.[0]; if (!f) return;
                  await attachFile(f, "video");
                  e.currentTarget.value = "";
                }} />
              </label>
              <label className={`flex flex-col items-center justify-center gap-1 text-xs rounded-md border border-input bg-background px-2 py-3 cursor-pointer hover:border-primary/40 transition ${uploading === "voice" ? "opacity-60" : ""}`}>
                {uploading === "voice" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
                <span>Voice note</span>
                <input type="file" accept="audio/*" className="hidden" disabled={uploading !== null} onChange={async e => {
                  const f = e.target.files?.[0]; if (!f) return;
                  await attachFile(f, "voice");
                  e.currentTarget.value = "";
                }} />
              </label>
            </div>
            {uploadError && (
              <div role="alert" data-testid="vb-upload-error" className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
                <div className="flex-1">
                  <div className="font-medium">Couldn't add {uploadError.kind}</div>
                  <div className="opacity-90">{uploadError.message}</div>
                </div>
                <div className="flex gap-1">
                  {uploadError.retryable && (
                    <Button size="sm" variant="outline" onClick={() => {
                      // Re-open the picker for the same kind so the user can retry.
                      const sel = document.querySelector<HTMLInputElement>(`input[type="file"][accept^="${uploadError.kind === "voice" ? "audio" : uploadError.kind}"]`);
                      sel?.click();
                    }}>Retry</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setUploadError(null)} aria-label="Dismiss upload error">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            {draft.imageUrl && <img src={draft.imageUrl} alt="" className="rounded-lg max-h-40 object-cover w-full" />}
            {draft.videoUrl && <video src={draft.videoUrl} controls className="rounded-lg max-h-40 w-full" />}
            {draft.voiceNote && <audio src={draft.voiceNote} controls className="w-full" />}
            {!userId && (draft.imageUrl || draft.videoUrl || draft.voiceNote) && (
              <p className="text-[11px] text-amber-400">Sign in to sync this media across devices and sessions.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={saveDream}><Save className="h-4 w-4" /> Save dream</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

// =========================
// SECTION 3 — Life Wheel
// =========================
function SectionLifeWheel({ wheel, setWheel }: { wheel: WheelArea[]; setWheel: (w: WheelArea[]) => void }) {
  const update = (i: number, patch: Partial<WheelArea>) => {
    const next = [...wheel];
    next[i] = { ...next[i], ...patch };
    setWheel(next);
  };

  return (
    <Section eyebrow="Section 03" title="Life Wheel"
      subtitle="Eight dimensions of a whole life. Score now, set the target, watch it grow.">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {wheel.map((w, i) => {
          const pct = (w.current / 10) * 100;
          const targetPct = (w.target / 10) * 100;
          const circ = 2 * Math.PI * 42;
          return (
            <GlassCard key={w.key} className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-base font-semibold">{w.label}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Target {w.target}/10</div>
              </div>
              <div className="relative mx-auto w-32 h-32">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke={w.color} strokeWidth="7" strokeLinecap="round"
                    strokeDasharray={circ} strokeDashoffset={circ * (1 - pct/100)}
                    style={{ transition: "stroke-dashoffset 0.7s ease" }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="font-display text-3xl font-bold">{w.current}</div>
                  <div className="text-[10px] text-muted-foreground">of 10</div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="text-[11px] text-muted-foreground">Current</div>
                <Slider min={0} max={10} step={1} value={[w.current]} onValueChange={([v]) => update(i, { current: v })} />
                <div className="text-[11px] text-muted-foreground">Target</div>
                <Slider min={0} max={10} step={1} value={[w.target]} onValueChange={([v]) => update(i, { target: v })} />
                <div className="text-[11px] text-primary/90 pt-1">
                  💡 {w.target - w.current > 3 ? "Big leap — set one micro-habit this week." : w.target > w.current ? "Steady gains — schedule a weekly review." : "You're at your target. Hold the line."}
                </div>
              </div>
            </GlassCard>
          );
        })}
      </div>
    </Section>
  );
}

// =========================
// SECTION 4 — Purpose Builder
// =========================
function SectionPurpose({ purpose, setPurpose, identity }: {
  purpose: PurposePack | null; setPurpose: (p: PurposePack | null) => void;
  identity: ReturnType<typeof deriveIdentity>;
}) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Tell me what kind of impact you want to make"); return; }
    setLoading(true);
    try {
      // `ai-coach` streams (SSE) and takes its instructions through `context`,
      // so the purpose builder uses the same transport as the YoMA panel.
      const sys = `You are YOMA, a warm but precise life-purpose coach. Output strict JSON only — no prose, no code fences — with keys: purpose, mission, vision, values (array of 5 short words), motto, brand, leadership, futureIdentity. Keep each <= 30 words.`;
      const userMsg = `User strengths: ${identity.strengths.join(", ")}. Archetype: ${identity.archetype}. Their intent: ${prompt}`;
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token ?? import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const text = await streamYomaReply({
        url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-coach`,
        token,
        context: sys,
        turns: [{ role: "user", content: userMsg }],
      });
      let pack: PurposePack | null = null;
      try {
        const match = text.match(/\{[\s\S]*\}/);
        if (match) pack = JSON.parse(match[0]);
      } catch { /* ignore */ }
      if (!pack) {
        // Fallback locally
        pack = {
          purpose: `Use ${identity.strengths[0] ?? "your gifts"} to ${prompt}.`,
          mission: `Show up daily as a ${identity.archetype.toLowerCase()}, building skills and helping people grow.`,
          vision: `A world where I have measurably shifted the lives of 1,000+ people through this work.`,
          values: identity.values.slice(0,5),
          motto: "Small actions. Every day. Extraordinary future.",
          brand: `${identity.archetype} who turns ideas into outcomes.`,
          leadership: "Lead by listening, then doing — quietly excellent.",
          futureIdentity: `A respected ${identity.archetype.toLowerCase()} known for ${identity.strengths.slice(0,2).join(" & ")}.`,
        };
      }
      setPurpose(archivePurposeVersion(purpose, pack));
      toast.success("Purpose generated");
    } catch (e) {
      toast.error("Couldn't reach the AI. Saved a starter for you.");
      setPurpose(archivePurposeVersion(purpose, {
        purpose: `Use my ${identity.strengths[0] ?? "strengths"} to ${prompt || "make a meaningful impact"}.`,
        mission: "Show up daily, ship work that matters, and lift others as I climb.",
        vision: "A future where my work changes lives.",
        values: identity.values.slice(0,5),
        motto: "Small actions. Every day.",
        brand: `${identity.archetype}`,
        leadership: "Listen first. Then build.",
        futureIdentity: `A respected ${identity.archetype.toLowerCase()}.`,
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section id="purpose" eyebrow="Section 04" title="AI Purpose Builder"
      subtitle="The signature WeKIT™ experience. One prompt — your complete purpose architecture.">
      <GlassCard className="p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="What kind of impact do you want to make?"
            className="text-base h-12 bg-white/[0.04] border-white/10"
          />
          <Button size="lg" onClick={generate} disabled={loading} className="rounded-full">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Generate
          </Button>
        </div>

        {purpose && (
          <PurposeEditor purpose={purpose} setPurpose={setPurpose} regenerate={generate} />
        )}
      </GlassCard>
    </Section>
  );
}

function PurposeEditor({ purpose, setPurpose, regenerate }: {
  purpose: PurposePack; setPurpose: (p: PurposePack | null) => void; regenerate: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PurposePack>(purpose);
  useEffect(() => { setDraft(purpose); }, [purpose]);

  const fields: Array<[string, keyof PurposePack]> = [
    ["Purpose Statement", "purpose"],
    ["Mission", "mission"],
    ["Vision", "vision"],
    ["Life Motto", "motto"],
    ["Personal Brand", "brand"],
    ["Leadership Style", "leadership"],
    ["Future Identity", "futureIdentity"],
  ];

  const setField = (k: keyof PurposePack, v: string) => setDraft({ ...draft, [k]: v } as PurposePack);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
      className="mt-8 grid md:grid-cols-2 gap-4">
      {fields.map(([label, k]) => (
        <div key={label} className="rounded-2xl p-4 border border-white/10 bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-primary/80">{label}</div>
          {editing ? (
            <Textarea
              value={(draft[k] as string) ?? ""}
              onChange={e => setField(k, e.target.value)}
              className="mt-2 bg-background/40"
              rows={2}
            />
          ) : (
            <div className="mt-2 font-display text-base leading-relaxed">{purpose[k] as string}</div>
          )}
        </div>
      ))}
      <div className="rounded-2xl p-4 border border-white/10 bg-white/[0.02]">
        <div className="text-[10px] uppercase tracking-[0.2em] text-primary/80">Core Values</div>
        {editing ? (
          <Input
            className="mt-2 bg-background/40"
            value={draft.values.join(", ")}
            onChange={e => setDraft({ ...draft, values: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
            placeholder="comma separated"
          />
        ) : (
          <div className="mt-2 font-display text-base leading-relaxed">{purpose.values.join(" · ")}</div>
        )}
      </div>
      <div className="md:col-span-2 flex flex-wrap gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={() => setPurpose(null)}>Clear</Button>
        <PurposeHistoryButton purpose={purpose} setPurpose={setPurpose} />
        {editing ? (
          <>
            <Button variant="ghost" size="sm" onClick={() => { setDraft(purpose); setEditing(false); }}>Cancel</Button>
            <Button size="sm" onClick={() => {
              setPurpose(archivePurposeVersion(purpose, draft));
              setEditing(false);
              toast.success("Purpose saved — previous version kept in history");
            }}>
              <Save className="h-4 w-4" /> Save
            </Button>
          </>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Edit3 className="h-4 w-4" /> Edit
          </Button>
        )}
        <Button size="sm" onClick={regenerate} variant="outline">
          <Wand2 className="h-4 w-4" /> Regenerate
        </Button>
      </div>
    </motion.div>
  );
}

const HISTORY_PAGE_SIZE = 5;

function DiffView({ prev, next }: { prev: string; next: string }) {
  const segs = diffWords(prev || "", next || "");
  if (!segs.length) return <span className="text-muted-foreground">—</span>;
  return (
    <span>
      {segs.map((s, i) => {
        if (s.type === "equal") return <span key={i}>{s.text}</span>;
        if (s.type === "added") return <span key={i} className="bg-emerald-500/15 text-emerald-300 rounded px-0.5">{s.text}</span>;
        return <span key={i} className="bg-rose-500/15 text-rose-300 line-through rounded px-0.5">{s.text}</span>;
      })}
    </span>
  );
}

function PurposeHistoryButton({ purpose, setPurpose }: {
  purpose: PurposePack; setPurpose: (p: PurposePack | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [compareIdx, setCompareIdx] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const versions = purpose._versions ?? [];
  const totalPages = Math.max(1, Math.ceil(versions.length / HISTORY_PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = safePage * HISTORY_PAGE_SIZE;
  const visible = versions.slice(pageStart, pageStart + HISTORY_PAGE_SIZE);
  const atCap = versions.length >= MAX_PURPOSE_VERSIONS;

  const restore = (idx: number) => {
    setPurpose(restorePurposeVersion(purpose, idx) as PurposePack);
    setOpen(false);
    toast.success("Restored — current version archived so you can undo");
  };

  const fields: Array<[string, keyof PurposePack]> = [
    ["Purpose", "purpose"],
    ["Mission", "mission"],
    ["Vision", "vision"],
    ["Motto", "motto"],
    ["Brand", "brand"],
    ["Leadership", "leadership"],
    ["Future Identity", "futureIdentity"],
  ];

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => { setOpen(true); setPage(0); }}>
        <History className="h-4 w-4" /> History {versions.length ? `(${versions.length})` : ""}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Purpose version history</DialogTitle>
            <DialogDescription>
              Every regenerate and edit is archived. View, compare, or restore an older version — restoring auto-archives the current one.
            </DialogDescription>
          </DialogHeader>
          <div data-testid="vb-history-cap-notice" className={`mt-2 rounded-md border px-3 py-2 text-xs ${atCap ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-white/10 bg-white/[0.03] text-muted-foreground"}`}>
            {atCap
              ? `History is at the cap of ${MAX_PURPOSE_VERSIONS}. Saving a new version drops the oldest.`
              : `Saving up to ${MAX_PURPOSE_VERSIONS} versions — ${versions.length} stored, ${MAX_PURPOSE_VERSIONS - versions.length} remaining.`}
          </div>
          {versions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No prior versions yet. Edit or regenerate to start your history.
            </p>
          ) : (
            <div className="space-y-3 mt-3" data-testid="vb-history-list">
              {visible.map((v, idxOnPage) => {
                const i = pageStart + idxOnPage;
                const expanded = compareIdx === i;
                return (
                  <div key={v.savedAt} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-primary/80">Version {versions.length - i}</div>
                        <div className="text-xs text-muted-foreground">{new Date(v.savedAt).toLocaleString()}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setCompareIdx(expanded ? null : i)}>
                          {expanded ? "Hide" : "Compare"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => restore(i)}>
                          <RotateCcw className="h-4 w-4" /> Restore
                        </Button>
                      </div>
                    </div>
                    <p className="mt-2 text-sm line-clamp-2">{v.pack.purpose}</p>
                    {expanded && (
                      <div className="mt-3 space-y-2 text-xs" data-testid="vb-history-compare">
                        <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-wider text-muted-foreground">
                          <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded bg-rose-500/60" /> removed</span>
                          <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded bg-emerald-500/60" /> added</span>
                          <span className="ml-auto">archived → current</span>
                        </div>
                        {fields.map(([label, k]) => (
                          <div key={label} className="rounded-lg border border-white/10 p-3">
                            <div className="flex items-center justify-between">
                              <div className="text-[10px] uppercase tracking-wider text-primary/70">{label}</div>
                            </div>
                            <div className="mt-2 grid sm:grid-cols-2 gap-3">
                              <div>
                                <div className="text-[10px] uppercase text-muted-foreground mb-1">Archived</div>
                                <div className="leading-relaxed">{(v.pack[k] as string) || <span className="text-muted-foreground">—</span>}</div>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase text-muted-foreground mb-1">Current (diff)</div>
                                <div className="leading-relaxed"><DiffView prev={(v.pack[k] as string) || ""} next={(purpose[k] as string) || ""} /></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-2" data-testid="vb-history-pagination">
                  <Button variant="ghost" size="sm" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>
                    ← Newer
                  </Button>
                  <span className="text-xs text-muted-foreground">Page {safePage + 1} of {totalPages}</span>
                  <Button variant="ghost" size="sm" disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)}>
                    Older →
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}





// =========================
// SECTION 5 — Timeline
// =========================
type Milestone = {
  id: string;
  label: string;
  detail: string;
  skills: string[];
  months: number;
  courses: string;
  mentors: string;
};

const TIMELINE_DEFAULT: Milestone[] = [
  { id: "m1", label: "Grade 10", detail: "Foundations & curiosity", skills: ["Study skills", "Self-awareness"], months: 0, courses: "Coursera, NPTEL, edX foundations", mentors: "Find one in our community → /refer" },
  { id: "m2", label: "Choose Subjects", detail: "Align studies to strengths", skills: ["Research", "Decision-making"], months: 4, courses: "Coursera, NPTEL, edX foundations", mentors: "Find one in our community → /refer" },
  { id: "m3", label: "Build Portfolio", detail: "Ship your first projects", skills: ["Creativity", "Communication"], months: 10, courses: "Coursera, NPTEL, edX foundations", mentors: "Find one in our community → /refer" },
  { id: "m4", label: "Certification", detail: "First credential", skills: ["Discipline"], months: 14, courses: "Coursera, NPTEL, edX foundations", mentors: "Find one in our community → /refer" },
  { id: "m5", label: "Internship", detail: "Real-world reps", skills: ["Teamwork", "Domain knowledge"], months: 18, courses: "Coursera, NPTEL, edX foundations", mentors: "Find one in our community → /refer" },
  { id: "m6", label: "College", detail: "Depth + network", skills: ["Critical thinking"], months: 24, courses: "Coursera, NPTEL, edX foundations", mentors: "Find one in our community → /refer" },
  { id: "m7", label: "Masters / Specialization", detail: "Expertise compounds", skills: ["Research"], months: 60, courses: "Coursera, NPTEL, edX foundations", mentors: "Find one in our community → /refer" },
  { id: "m8", label: "First Job", detail: "Skills become income", skills: ["Execution"], months: 84, courses: "Coursera, NPTEL, edX foundations", mentors: "Find one in our community → /refer" },
  { id: "m9", label: "Leadership Role", detail: "Lift others as you climb", skills: ["Leadership", "Coaching"], months: 120, courses: "Coursera, NPTEL, edX foundations", mentors: "Find one in our community → /refer" },
  { id: "m10", label: "Startup / Impact Project", detail: "Build something only you can", skills: ["Entrepreneurship"], months: 150, courses: "Coursera, NPTEL, edX foundations", mentors: "Find one in our community → /refer" },
  { id: "m11", label: "Financial Independence", detail: "Time freedom", skills: ["Money"], months: 200, courses: "Coursera, NPTEL, edX foundations", mentors: "Find one in our community → /refer" },
  { id: "m12", label: "Mentor Others", detail: "Pay it forward", skills: ["Legacy"], months: 240, courses: "Coursera, NPTEL, edX foundations", mentors: "Find one in our community → /refer" },
];

function SectionTimeline() {
  const [open, setOpen] = useState<number | null>(null);
  const [items, setItems] = useState<Milestone[]>(() => load<Milestone[]>(LS.timeline, TIMELINE_DEFAULT));
  const [editing, setEditing] = useState<Milestone | null>(null);
  const [isNew, setIsNew] = useState(false);
  useEffect(() => { save(LS.timeline, items); }, [items]);

  const startEdit = (m: Milestone) => { setEditing({ ...m }); setIsNew(false); };
  const startAdd = () => {
    setEditing({ id: uid(), label: "New milestone", detail: "", skills: [], months: 0, courses: "", mentors: "" });
    setIsNew(true);
  };
  const saveEdit = () => {
    if (!editing) return;
    if (!editing.label.trim()) { toast.error("Label is required"); return; }
    if (isNew) {
      setItems([...items, editing]);
      toast.success("Milestone added");
    } else {
      setItems(items.map(m => m.id === editing.id ? editing : m));
      toast.success("Milestone updated");
    }
    setEditing(null);
  };
  const deleteItem = (id: string) => {
    const prev = items;
    const removed = items.find(m => m.id === id);
    setItems(items.filter(m => m.id !== id));
    setOpen(null);
    if (removed) {
      toast.success(`Removed "${removed.label}"`, {
        action: { label: "Undo", onClick: () => setItems(prev) },
      });
    }
  };
  const resetAll = () => {
    setItems(TIMELINE_DEFAULT);
    setOpen(null);
    toast.info("Timeline reset to defaults");
  };

  return (
    <Section eyebrow="Section 05" title="Future Timeline"
      subtitle="Your life on rails — milestones, mentors, skills, every step zoomable.">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button size="sm" variant="outline" className="min-h-11 px-4" onClick={startAdd}><Plus className="h-3.5 w-3.5 mr-1" />Add milestone</Button>
        <Button size="sm" variant="ghost" className="min-h-11 px-4" onClick={resetAll}>Reset to defaults</Button>
      </div>
      <div className="overflow-x-auto -mx-4 sm:mx-0 pb-4">
        <div className="flex gap-3 px-4 sm:px-0 min-w-max">
          {items.map((m, i) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className={`w-56 rounded-2xl p-4 border transition relative group ${
                open === i ? "border-primary/60 bg-primary/5" : "border-white/10 bg-white/[0.03] hover:border-white/20"
              }`}
            >
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full text-left">
                <div className="text-[10px] uppercase tracking-wider text-primary/80">Milestone {i+1}</div>
                <div className="mt-1 font-display text-base font-semibold pr-12">{m.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{m.detail}</div>
                <ChevronDown className={`h-4 w-4 mt-3 transition ${open === i ? "rotate-180 text-primary" : "text-muted-foreground"}`} />
              </button>
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                <button onClick={() => startEdit(m)} aria-label="Edit milestone" className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-background/60 hover:bg-background border border-white/10">
                  <Pencil className="h-3 w-3" />
                </button>
                <button onClick={() => deleteItem(m.id)} aria-label="Delete milestone" className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-background/60 hover:bg-destructive/20 border border-white/10">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      <AnimatePresence>
        {open !== null && items[open] && (
          <motion.div
            key={open}
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="mt-4">
            <GlassCard className="p-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-primary/80">Skills</div>
                  <div className="mt-1">{items[open].skills.join(", ") || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-primary/80">Estimated timeline</div>
                  <div className="mt-1">~{items[open].months} months from start</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-primary/80">Suggested courses</div>
                  <div className="mt-1">{items[open].courses || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-primary/80">Mentors</div>
                  <div className="mt-1">{items[open].mentors || "—"}</div>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(items[open])}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => deleteItem(items[open].id)}><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</Button>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isNew ? "Add milestone" : "Edit milestone"}</DialogTitle>
            <DialogDescription>Personalize your future timeline — these changes are saved on this device.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Label</label>
                <Input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Detail</label>
                <Input value={editing.detail} onChange={(e) => setEditing({ ...editing, detail: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">Months from start</label>
                  <Input type="number" min={0} value={editing.months}
                    onChange={(e) => setEditing({ ...editing, months: Math.max(0, parseInt(e.target.value) || 0) })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">Skills (comma-separated)</label>
                  <Input value={editing.skills.join(", ")}
                    onChange={(e) => setEditing({ ...editing, skills: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Suggested courses</label>
                <Input value={editing.courses} onChange={(e) => setEditing({ ...editing, courses: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Mentors</label>
                <Input value={editing.mentors} onChange={(e) => setEditing({ ...editing, mentors: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit}>{isNew ? "Add" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

// =========================
// SECTION 6 — Action Engine
// =========================
function SectionActions({ actions, setActions, archives, setArchives }: {
  actions: ActionPlan[]; setActions: (a: ActionPlan[]) => void;
  archives: ArchivedPlan[]; setArchives: (a: ArchivedPlan[]) => void;
}) {
  const [showArchives, setShowArchives] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ArchivedPlan | null>(null);

  const nextVersionFor = (dreamId: string) =>
    (archives.filter(a => a.dreamId === dreamId).reduce((m, a) => Math.max(m, a.version), 0)) + 1;

  const restoreArchive = (archive: ArchivedPlan) => {
    const { archivedAt: _a, reason: _r, version: _v, ...plan } = archive;
    const current = actions.find(p => p.dreamId === plan.dreamId);
    let nextArchives = archives.filter(a => !(a.dreamId === archive.dreamId && a.archivedAt === archive.archivedAt));
    // If a different plan is currently active for this dream, archive it so
    // restoring never silently loses the user's in-progress work.
    if (current) {
      const supersededVersion = nextVersionFor(plan.dreamId);
      nextArchives = [
        { ...current, archivedAt: Date.now(), reason: "regenerated", version: supersededVersion },
        ...nextArchives,
      ];
    }
    setArchives(nextArchives);
    setActions([plan, ...actions.filter(p => p.dreamId !== plan.dreamId)]);
    toast.success(`Restored “Path to ${plan.dreamTitle}” v${archive.version}`, {
      description: current
        ? `Previous active plan (${current.steps.filter(s => s.done).length}/${current.steps.length} done) archived for safekeeping.`
        : "Now active in your Action Engine.",
    });
    document.getElementById("actions")?.scrollIntoView({ behavior: "smooth" });
  };

  const confirmDeleteArchive = () => {
    if (!pendingDelete) return;
    const removed = pendingDelete;
    setArchives(archives.filter(a => !(a.dreamId === removed.dreamId && a.archivedAt === removed.archivedAt)));
    setPendingDelete(null);
    toast.success(`Archived roadmap “${removed.dreamTitle}” v${removed.version} deleted`, {
      description: "You can undo for a few seconds.",
      duration: 8000,
      action: {
        label: "Undo",
        onClick: () => {
          setArchives([removed, ...archives.filter(a => !(a.dreamId === removed.dreamId && a.archivedAt === removed.archivedAt))]);
          toast.info("Delete undone");
        },
      },
    });
  };

  const toggle = (planIdx: number, stepIdx: number) => {
    const next = actions.map((p, pi) => pi !== planIdx ? p : {
      ...p, steps: p.steps.map((s, si) => si === stepIdx ? { ...s, done: !s.done } : s)
    });
    setActions(next);
  };
  return (
    <Section id="actions" eyebrow="Section 06" title="Action Engine"
      subtitle="Every dream becomes a step-by-step path. Tick. Stack. Win.">
      {actions.length === 0 ? (
        <GlassCard className="p-10 text-center">
          <Wand2 className="h-8 w-8 mx-auto text-primary mb-3" />
          <div className="font-display text-lg">Generate a plan from one of your dreams above</div>
          <div className="text-sm text-muted-foreground mt-1">Click "Generate plan" on any dream card.</div>
        </GlassCard>
      ) : (
        <div className="space-y-6">
          {actions.map((p, pi) => {
            const done = p.steps.filter(s => s.done).length;
            const pct = Math.round((done / p.steps.length) * 100);
            return (
              <GlassCard key={p.dreamId} className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-primary/80">Path to</div>
                    <div className="font-display text-xl font-semibold">{p.dreamTitle}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">{done} / {p.steps.length} done</div>
                    <div className="font-display text-lg">{pct}%</div>
                  </div>
                </div>
                <Progress value={pct} className="h-1.5 mb-4" />
                <div className="space-y-2">
                  {p.steps.map((s, si) => (
                    <button key={s.id} onClick={() => toggle(pi, si)}
                      className={`w-full flex items-center justify-between gap-3 p-3 rounded-xl border text-left transition ${
                        s.done ? "border-primary/30 bg-primary/5" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
                      }`}>
                      <div className="flex items-center gap-3 min-w-0">
                        {s.done ? <CheckCircle2 className="h-4 w-4 text-primary shrink-0" /> : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                        <span className={`text-sm ${s.done ? "line-through text-muted-foreground" : ""}`}>{s.label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-muted-foreground shrink-0">
                        <span className="hidden sm:inline">{"🔥".repeat(s.difficulty)}</span>
                        <span>{s.hours}h</span>
                        <Badge variant="outline" className="text-[10px]">+{s.xp} XP</Badge>
                      </div>
                    </button>
                  ))}
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}

      {archives.length > 0 && (
        <div className="mt-8">
          <button
            onClick={() => setShowArchives(v => !v)}
            className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-primary transition"
          >
            <Badge variant="outline" className="text-[10px]">{archives.length}</Badge>
            Archived roadmaps {showArchives ? "▾" : "▸"}
          </button>
          {showArchives && (
            <div className="mt-4 space-y-3">
              {archives.map((a, i) => {
                const done = a.steps.filter(s => s.done).length;
                return (
                  <GlassCard key={`${a.dreamId}-${a.archivedAt}`} className="p-4 opacity-80">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          v{a.version} · Archived · {a.reason === "dream_changed" ? "dream changed" : "regenerated"} · {new Date(a.archivedAt).toLocaleDateString()}
                        </div>
                        <div className="font-display text-base truncate">Path to {a.dreamTitle}</div>
                        <div className="text-xs text-muted-foreground">{done} / {a.steps.length} steps completed before archive</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => restoreArchive(a)}>
                          <RotateCcw className="h-3.5 w-3.5" /> Restore
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setPendingDelete(a)}>Delete</Button>
                      </div>
                    </div>
                  </GlassCard>
                );
              })}
            </div>
          )}
        </div>
      )}



      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete archived roadmap?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && (
                <>This will remove the archived roadmap <strong>“Path to {pendingDelete.dreamTitle}” v{pendingDelete.version}</strong> ({pendingDelete.steps.filter(s => s.done).length}/{pendingDelete.steps.length} steps done). You'll have a few seconds to undo.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteArchive}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  );
}

// =========================
// SECTION 7 — Opportunities
// =========================
function SectionOpportunities({ identity, dreams, navigate }: {
  identity: ReturnType<typeof deriveIdentity>; dreams: Dream[];
  navigate: (path: string) => void;
}) {
  const base = [
    { icon: GraduationCap, kind: "Scholarship", title: "Inspire-MANAK / National Talent grants", note: "Open annually, merit + need based." },
    { icon: Briefcase, kind: "Internship", title: `${dreams[0]?.title ?? "Industry"} apprenticeships`, note: "Search LinkedIn + AICTE Internshala." },
    { icon: Trophy, kind: "Competition", title: "Smart India Hackathon", note: "Team-based, national stage." },
    { icon: Rocket, kind: "Hackathon", title: "Major League Hacking (MLH)", note: "Global weekend builds." },
    { icon: BookOpen, kind: "Course", title: "Coursera Career Certificates", note: "Google, IBM, Meta tracks." },
    { icon: Heart, kind: "Volunteering", title: "Teach For India / United Way", note: "Build leadership through service." },
    { icon: Users, kind: "Mentors", title: "WeKIT mentor pool", note: "Match based on archetype + dreams." },
    { icon: Globe, kind: "Community", title: `${identity.archetype} community`, note: "Online + local meetups." },
    { icon: MapPin, kind: "Future Careers", title: "Explore 5,000+ careers of the future", note: "Filtered to your DNA match.", to: "/careers" },
  ];
  return (
    <Section eyebrow="Section 07" title="Opportunity Engine"
      subtitle="Hand-picked openings matched to your strengths and pinned dreams.">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {base.map((o, i) => (
          <GlassCard key={i} className="p-5 hover:border-primary/30 transition cursor-pointer"
            onClick={() => o.to && navigate(o.to)}>
            <div className="flex items-center gap-2">
              <o.icon className="h-4 w-4 text-primary" />
              <Badge variant="outline" className="text-[10px]">{o.kind}</Badge>
            </div>
            <div className="mt-3 font-display text-base font-semibold">{o.title}</div>
            <div className="text-xs text-muted-foreground mt-1">{o.note}</div>
            {o.to && (
              <div className="mt-3 text-xs text-primary inline-flex items-center gap-1">
                Explore <ArrowRight className="h-3 w-3" />
              </div>
            )}
          </GlassCard>
        ))}
      </div>
    </Section>
  );
}

// =========================
// SECTION 8 — Gallery
// =========================
function SectionGallery({ gallery, setGallery, userId }: { gallery: GalleryItem[]; setGallery: (g: GalleryItem[]) => void; userId: string | null }) {
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [galleryError, setGalleryError] = useState<{ type: "image" | "video"; message: string; retryable: boolean; lastFile: File | null } | null>(null);
  const addQuote = () => {
    if (!text.trim()) return;
    setGallery([{ id: uid(), type: "quote", text }, ...gallery]);
    setText("");
  };
  const addMedia = async (file: File, type: "image" | "video") => {
    setUploading(true);
    setGalleryError(null);
    try {
      if (userId) {
        const { path, url } = await uploadVisionMedia(userId, file);
        setGallery([{ id: uid(), type, src: url, path }, ...gallery]);
        toast.success(`${type === "image" ? "Image" : "Video"} synced`);
      } else {
        const reader = new FileReader();
        const url = await new Promise<string>((res) => { reader.onload = () => res(reader.result as string); reader.readAsDataURL(file); });
        setGallery([{ id: uid(), type, src: url }, ...gallery]);
      }
    } catch (e) {
      console.error(e);
      const info = describeUploadError(e, { size: file.size, type: file.type });
      setGalleryError({ type, message: info.message, retryable: info.retryable, lastFile: info.retryable ? file : null });
      toast.error(info.title, {
        description: info.message,
        action: info.retryable ? { label: "Retry", onClick: () => { void addMedia(file, type); } } : undefined,
      });
    } finally { setUploading(false); }
  };
  const remove = (id: string) => setGallery(gallery.filter(i => i.id !== id));

  return (
    <Section eyebrow="Section 08" title="My Dream Gallery"
      subtitle="Pinterest meets Apple. Drop in anything that fuels you.">
      <div className="flex flex-wrap gap-2 mb-6">
        <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/10 text-sm cursor-pointer ${uploading ? "opacity-60" : ""}`}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} Add image
          <input type="file" accept="image/*" hidden disabled={uploading} onChange={e => { const f = e.target.files?.[0]; if (f) addMedia(f, "image"); e.currentTarget.value = ""; }} />
        </label>
        <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/10 text-sm cursor-pointer ${uploading ? "opacity-60" : ""}`}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />} Add video
          <input type="file" accept="video/*" hidden disabled={uploading} onChange={e => { const f = e.target.files?.[0]; if (f) addMedia(f, "video"); e.currentTarget.value = ""; }} />
        </label>
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Input placeholder='Add a quote or vision text…' value={text} onChange={e => setText(e.target.value)} />
          <Button className="min-h-11 px-4" onClick={addQuote}><Plus className="h-4 w-4" /> Pin</Button>
        </div>
      </div>
      {galleryError && (
        <div role="alert" data-testid="vb-gallery-upload-error" className="mb-4 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
          <div className="flex-1">
            <div className="font-medium">Couldn't add {galleryError.type}</div>
            <div className="opacity-90">{galleryError.message}</div>
          </div>
          <div className="flex gap-1">
            {galleryError.retryable && galleryError.lastFile && (
              <Button size="sm" variant="outline" onClick={() => { const f = galleryError.lastFile; if (f) void addMedia(f, galleryError.type); }}>Retry</Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setGalleryError(null)} aria-label="Dismiss upload error"><X className="h-4 w-4" /></Button>
          </div>
        </div>
      )}


      {gallery.length === 0 ? (
        <GlassCard className="p-10 text-center text-muted-foreground">Your gallery is a blank canvas — start pinning.</GlassCard>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
          {gallery.map(item => (
            <motion.div key={item.id} layout
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              className="mb-4 break-inside-avoid">
              <div className="relative group rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
                {item.type === "image" && item.src && (
                  <img src={item.src} alt="" className="w-full" />
                )}
                {item.type === "video" && item.src && (
                  <video src={item.src} controls className="w-full bg-black" />
                )}
                {item.type === "quote" && item.text && (
                  <div className="p-6 font-display text-lg leading-snug bg-gradient-to-br from-primary/10 via-transparent to-accent/10">
                    "{item.text}"
                  </div>
                )}
                <button onClick={() => remove(item.id)} aria-label="Remove this item from your vision board"
                  className="absolute top-2 right-2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/60 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition">
                  <Trash2 className="h-3 w-3 text-white" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </Section>
  );
}

// =========================
// SECTION 9 — YOMA floating
// =========================
function FloatingYoma({ identity, dreams, actions, wheel, streak }: {
  identity: ReturnType<typeof deriveIdentity>; dreams: Dream[]; actions: ActionPlan[];
  wheel: WheelArea[]; streak: { count: number; last: string };
}) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  // The thread is restored from cloud state, so closing the panel (or moving
  // to another device) resumes the same conversation rather than starting over.
  const [msgs, setMsgs] = useState<YomaTurn[]>(() => loadYomaThread());
  const [loading, setLoading] = useState(false);

  useEffect(() => { saveYomaThread(msgs); }, [msgs]);


  // Dynamic nudges grounded in dreams + Life Wheel + action progress
  const nudges = useMemo(() => {
    const out: string[] = [];
    const totalSteps = actions.reduce((s,a)=>s + a.steps.length, 0);
    const doneSteps = actions.reduce((s,a)=>s + a.steps.filter(x=>x.done).length, 0);
    if (totalSteps > 0) {
      const pct = Math.round(doneSteps / totalSteps * 100);
      out.push(`You've completed ${pct}% of your roadmap — momentum compounds.`);
    }
    if (dreams[0]) {
      out.push(`Pinned dream: "${dreams[0].title}". Today, do one 20-minute action toward it.`);
    } else {
      out.push("You haven't pinned a dream yet — start with one. Tiny is fine.");
    }
    // Life Wheel: find weakest area with a real gap
    const gapSorted = [...wheel].map(w => ({ ...w, gap: w.target - w.current }))
      .filter(w => w.gap > 0).sort((a,b) => b.gap - a.gap);
    if (gapSorted[0]) {
      out.push(`Your biggest Life Wheel gap is **${gapSorted[0].label}** (${gapSorted[0].current}→${gapSorted[0].target}). Pick one micro-habit for it this week.`);
    }
    if (streak.count >= 3) out.push(`🔥 ${streak.count}-day streak — don't break the chain.`);
    return out;
  }, [actions, dreams, wheel, streak]);

  const send = async () => {
    if (!input.trim()) return;
    const newMsgs: YomaTurn[] = [...msgs, { role: "user", content: input }];
    setMsgs(newMsgs); setInput(""); setLoading(true);
    try {
      // Rebuilt on every send: the reply must reflect the board as it is now,
      // not as it was when the panel was first opened.
      const wheelSummary = wheel.map(w => `${w.label} ${w.current}/${w.target}`).join(", ");
      const dreamList = dreams.map(d => `${d.title}${d.targetYear ? ` (by ${d.targetYear})` : ""}`).join("; ") || "none yet";
      const totalSteps = actions.reduce((s,a)=>s + a.steps.length, 0);
      const doneSteps = actions.reduce((s,a)=>s + a.steps.filter(x=>x.done).length, 0);
      const sys = `Tailor every reply to THIS user.
Archetype: ${identity.archetype}. Top strengths: ${identity.strengths.join(", ")}.
Pinned dreams: ${dreamList}.
Life Wheel (current/target out of 10): ${wheelSummary}.
Roadmap progress: ${doneSteps}/${totalSteps} steps. Streak: ${streak.count} days.
Be warm, concise, specific. Reference their dreams and weakest wheel area when relevant. Offer one concrete next action.`;

      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token ?? import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      setMsgs([...newMsgs, { role: "assistant", content: "" }]);
      const reply = await streamYomaReply({
        url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-coach`,
        token,
        context: sys,
        turns: newMsgs,
        onDelta: (full) => setMsgs([...newMsgs, { role: "assistant", content: full }]),
      });
      if (!reply.trim()) {
        setMsgs([...newMsgs, { role: "assistant", content: "I couldn't get a reply just then — try asking again." }]);
      }
    } catch {
      setMsgs([...newMsgs, { role: "assistant", content: "I'm having trouble reaching the network. But here's a thought: pick the smallest action you can take in the next 20 minutes and do it." }]);
    } finally {
      setLoading(false);
    }
  };



  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-primary to-accent shadow-2xl shadow-primary/30 flex items-center justify-center hover:scale-105 transition"
        aria-label="Open YOMA"
      >
        <Bot className="h-6 w-6 text-white" />
        <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-orange-400 animate-pulse" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-24 right-4 sm:right-6 z-40 w-[min(380px,calc(100vw-2rem))] max-h-[70vh] flex flex-col rounded-3xl border border-white/10 bg-background/95 backdrop-blur-xl shadow-2xl">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-primary" />
                <div className="font-display text-sm font-semibold">YOMA · Your AI coach</div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close YOMA coach" className="inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
              {msgs.length === 0 && (
                <div className="space-y-2">
                  {nudges.map((n,i) => (
                    <div key={i} className="rounded-xl bg-primary/5 border border-primary/15 p-3 text-xs">{n}</div>
                  ))}
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} className={`rounded-2xl p-3 max-w-[85%] ${m.role === "user" ? "bg-primary/15 ml-auto" : "bg-white/5"}`}>
                  {m.content}
                </div>
              ))}
              {loading && <div className="text-xs text-muted-foreground"><Loader2 className="h-3 w-3 inline animate-spin" /> Thinking…</div>}
            </div>
            <div className="p-3 border-t border-white/10 flex gap-2">
              <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder="Ask YOMA anything…" />
              <Button size="icon" className="min-h-11 min-w-11" aria-label="Send message to YOMA" onClick={send}><Send className="h-4 w-4" /></Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// =========================
// SECTION 10 — Daily Momentum
// =========================
function ScheduleEditor({ value, onChange }: { value: TaskSchedule; onChange: (s: TaskSchedule) => void }) {
  const kind = value.kind;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {([
          { k: "daily", label: "Every day" },
          { k: "weekly", label: "Certain days" },
          { k: "once", label: "One-off date" },
        ] as const).map((opt) => (
          <button
            key={opt.k}
            type="button"
            onClick={() =>
              onChange(
                opt.k === "daily"
                  ? { kind: "daily" }
                  : opt.k === "weekly"
                    ? { kind: "weekly", days: kind === "weekly" ? value.days : [1, 3, 5] }
                    : { kind: "once", date: kind === "once" ? value.date : todayKey() },
              )
            }
            aria-pressed={kind === opt.k}
            className={`min-h-11 rounded-xl border px-4 text-sm transition ${
              kind === opt.k
                ? "border-primary/40 bg-primary/10 text-foreground"
                : "border-white/10 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.06]"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {kind === "weekly" && (
        <div className="flex flex-wrap gap-2">
          {WEEKDAY_LABELS.map((label, day) => {
            const on = value.kind === "weekly" && value.days.includes(day);
            return (
              <button
                key={label}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  if (value.kind !== "weekly") return;
                  const days = on ? value.days.filter((d) => d !== day) : [...value.days, day];
                  onChange({ kind: "weekly", days });
                }}
                className={`min-h-11 min-w-11 rounded-xl border px-3 text-xs transition ${
                  on ? "border-primary/40 bg-primary/10" : "border-white/10 bg-white/[0.02] text-muted-foreground"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {kind === "once" && (
        <Input
          type="date"
          className="max-w-[12rem]"
          value={value.kind === "once" ? value.date : todayKey()}
          onChange={(e) => e.target.value && onChange({ kind: "once", date: e.target.value })}
        />
      )}
    </div>
  );
}

function SectionDaily({ book, setBook, streak, userId }: {
  book: DailyBook;
  setBook: React.Dispatch<React.SetStateAction<DailyBook>>;
  streak: { count: number; last: string };
  userId: string | null;
}) {
  const today = todayKey();
  const [date, setDate] = useState(today);
  const [manageOpen, setManageOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newSchedule, setNewSchedule] = useState<TaskSchedule>({ kind: "daily" });
  const [editing, setEditing] = useState<string | null>(null);
  const [filter, setFilter] = useState<HistoryFilter>(EMPTY_HISTORY_FILTER);
  const importRef = useRef<HTMLInputElement>(null);

  // Reminders live server-side so the nudge arrives even with the app closed.
  const [reminder, setReminder] = useState<ReminderSettings>(DEFAULT_REMINDER);
  const [remindSaving, setRemindSaving] = useState(false);
  useEffect(() => {
    if (!userId) return;
    fetchReminderSettings().then(setReminder).catch(() => { /* defaults stand */ });
  }, [userId]);

  // Edits already autosave, but a real Save button removes all doubt: it
  // flushes the activity list straight to the cloud and confirms it landed.
  const [activitiesSaving, setActivitiesSaving] = useState(false);
  const saveActivitiesNow = async () => {
    setEditing(null);
    if (!userId) {
      toast.success("Saved on this device. Sign in to sync across your devices.");
      setManageOpen(false);
      return;
    }
    setActivitiesSaving(true);
    try {
      const { error } = await supabase.from("vision_boards").upsert(
        {
          user_id: userId,
          daily: JSON.parse(JSON.stringify(book)),
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" },
      );
      if (error) throw error;
      toast.success("Your activities are saved.");
      setManageOpen(false);
    } catch (e) {
      console.warn("activity save failed", e);
      toast.error("Couldn't save just now — your changes are safe on this device. Try again.");
    } finally {
      setActivitiesSaving(false);
    }
  };



  const dayTasks = useMemo(() => tasksForDate(book, date), [book, date]);
  const log = logFor(book, date);
  const done = dayTasks.filter((t) => log.doneIds.includes(t.id)).length;
  const allDone = isDayComplete(book, date);
  const history = useMemo(() => historyEntries(book), [book]);
  const visibleHistory = useMemo(() => filterHistory(book, history, filter), [book, history, filter]);
  const isFuture = date > today;


  const [note, setNote] = useState(log.note ?? "");
  const [noteDirty, setNoteDirty] = useState(false);
  useEffect(() => {
    setNote(logFor(book, date).note ?? "");
    setNoteDirty(false);
  }, [date]); // eslint-disable-line

  const wasComplete = useRef(allDone);
  useEffect(() => {
    if (allDone && !wasComplete.current && date === today) {
      const next = computeStreak({ ...book }).count;
      toast.success(`${next}-day streak. +100 XP`);
      if (userId) {
        supabase.rpc("award_xp", {
          _user_id: userId,
          _event_type: "vb_daily_all_done",
          _points: 100,
          _dedupe_key: `vb:daily:all:${date}`,
          _related_id: null,
        });
      }
    }
    wasComplete.current = allDone;
  }, [allDone, date]); // eslint-disable-line

  const toggle = (taskId: string) => {
    if (isFuture) {
      toast.info("That day hasn't arrived yet — you can plan it, not tick it.");
      return;
    }
    const wasDone = log.doneIds.includes(taskId);
    setBook((b) => toggleTask(b, date, taskId));
    if (!wasDone && userId) {
      supabase.rpc("award_xp", {
        _user_id: userId,
        _event_type: "vb_daily_task",
        _points: 15,
        _dedupe_key: `vb:daily:task:${date}:${taskId}`,
        _related_id: null,
      });
    }
  };

  const saveNote = () => {
    setBook((b) => setNoteFor(b, date, note));
    setNoteDirty(false);
    toast.success(`Diary entry saved for ${formatDay(date)}.`);
  };

  /** A file you own: every activity, every day, every entry. */
  const doExport = () => {
    try {
      const blob = new Blob([JSON.stringify(exportPayload(book), null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFilename();
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded. Keep it somewhere safe.");
    } catch {
      toast.error("Couldn't create the backup file.");
    }
  };

  /** Bringing a backup onto a new device merges — it never wipes what's here. */
  const doImport = async (file: File) => {
    const result = parseImport(await file.text());
    if ("error" in result) {
      toast.error(result.error);
      return;
    }
    setBook((b) => mergeBooks(b, result.book));
    const days = Object.keys(result.book.logs).length;
    toast.success(`Backup merged — ${days} ${days === 1 ? "day" : "days"} restored.`);
  };


  const activeTasks = book.tasks.filter((t) => !t.archivedAt);
  const retiredTasks = book.tasks.filter((t) => t.archivedAt);

  return (
    <Section eyebrow="Section 10" title="Daily Momentum"
      subtitle="Your diary of small, doable, repeatable days — edit it, schedule it, look back on it.">
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
        <GlassCard className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="min-h-11 min-w-11" aria-label="Previous day"
                onClick={() => setDate((d) => shiftDay(d, -1))}>
                <ChevronRight className="h-4 w-4 rotate-180" />
              </Button>
              <div>
                <div className="font-display text-lg leading-tight">{formatDay(date)}</div>
                <div className="text-[11px] text-muted-foreground">{date}</div>
              </div>
              <Button variant="ghost" size="icon" className="min-h-11 min-w-11" aria-label="Next day"
                onClick={() => setDate((d) => shiftDay(d, 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              {date !== today && (
                <Button variant="ghost" size="sm" className="min-h-11" onClick={() => setDate(today)}>Today</Button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">{done} / {dayTasks.length}</span>
              <Button variant="outline" size="sm" className="min-h-11" onClick={() => setHistoryOpen(true)}>
                <History className="h-4 w-4 mr-1.5" /> History
              </Button>
              <Button variant="outline" size="sm" className="min-h-11" onClick={() => setRemindOpen(true)}>
                <Flame className="h-4 w-4 mr-1.5" /> Reminders
              </Button>
              <Button variant="outline" size="sm" className="min-h-11" onClick={() => setManageOpen(true)}>
                <SettingsIcon className="h-4 w-4 mr-1.5" /> Edit activities
              </Button>
            </div>

          </div>

          <div className="mt-4 space-y-2">
            {dayTasks.length === 0 && (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-sm text-muted-foreground">
                Nothing scheduled for this day. Add an activity to make it count.
              </div>
            )}
            {dayTasks.map((t) => {
              const isDone = log.doneIds.includes(t.id);
              return (
                <button key={t.id} onClick={() => toggle(t.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition ${
                    isDone ? "border-primary/30 bg-primary/5" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
                  }`}>
                  {isDone ? <CheckCircle2 className="h-4 w-4 text-primary shrink-0" /> : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                  <span className="min-w-0">
                    <span className={`block text-sm ${isDone ? "line-through text-muted-foreground" : ""}`}>{t.label}</span>
                    <span className="block text-[10px] text-muted-foreground">{describeSchedule(t.schedule)}</span>
                  </span>
                  <span className="ml-auto text-[10px] text-muted-foreground shrink-0">+{t.xp} XP</span>
                </button>
              );
            })}
          </div>

          <div className="mt-6">
            <label htmlFor="vb-diary" className="font-display text-sm">Diary — {formatDay(date)}</label>
            <p className="text-xs text-muted-foreground mt-0.5">
              What happened, what you noticed, what you'll carry into tomorrow.
            </p>
            <Textarea id="vb-diary" rows={4} className="mt-2" value={note}
              placeholder="Today I…"
              onChange={(e) => { setNote(e.target.value); setNoteDirty(true); }} />
            <div className="mt-2 flex items-center gap-2">
              <Button size="sm" className="min-h-11" onClick={saveNote} disabled={!noteDirty}>
                <Save className="h-4 w-4 mr-1.5" /> Save entry
              </Button>
              {log.savedAt > 0 && !noteDirty && (
                <span className="text-[11px] text-muted-foreground">Saved</span>
              )}
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6 flex flex-col items-center justify-center text-center">
          <Flame className="h-12 w-12 text-orange-400" />
          <div className="font-display text-5xl font-bold mt-3">{streak.count}</div>
          <div className="text-sm text-muted-foreground">day streak</div>
          <div className="mt-6 text-xs text-muted-foreground">Finish every scheduled activity to lock a day in.</div>
          <div className="mt-4 w-full text-left">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
              Your last few months
            </div>
            <DiaryHeatmap book={book} selected={date} onSelect={setDate} />
          </div>
          <div className="mt-4 text-xs text-muted-foreground">
            {history.length} {history.length === 1 ? "day" : "days"} in your diary
          </div>

          <AnimatePresence>
            {allDone && (
              <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }}
                className="mt-4 px-4 py-2 rounded-full bg-primary/15 border border-primary/30 text-xs font-display">
                ✨ {date === today ? "Locked. See you tomorrow, champion." : "This day was complete."}
              </motion.div>
            )}
          </AnimatePresence>
        </GlassCard>
      </div>

      {/* History / diary index */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Your diary</DialogTitle>
            <DialogDescription>Search your entries, or filter by activity. Tap a day to open it.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Input value={filter.query} aria-label="Search diary entries and activities"
              placeholder="Search words in your entries, or an activity…"
              onChange={(e) => setFilter((f) => ({ ...f, query: e.target.value }))} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <select aria-label="Filter by activity"
                className="min-h-11 rounded-xl border border-white/10 bg-background px-3 text-sm"
                value={filter.taskId}
                onChange={(e) => setFilter((f) => ({ ...f, taskId: e.target.value }))}>
                <option value="all">Any activity</option>
                {book.tasks.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              <select aria-label="Filter by day type"
                className="min-h-11 rounded-xl border border-white/10 bg-background px-3 text-sm"
                value={filter.scope}
                onChange={(e) => setFilter((f) => ({ ...f, scope: e.target.value as HistoryFilter["scope"] }))}>
                <option value="all">All days</option>
                <option value="complete">Complete days</option>
                <option value="incomplete">Partial days</option>
                <option value="with-notes">Days with a journal entry</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" aria-label="From date" value={filter.from ?? ""}
                onChange={(e) => setFilter((f) => ({ ...f, from: e.target.value || undefined }))} />
              <Input type="date" aria-label="To date" value={filter.to ?? ""}
                onChange={(e) => setFilter((f) => ({ ...f, to: e.target.value || undefined }))} />
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span>{visibleHistory.length} of {history.length} days</span>
              <Button variant="ghost" size="sm" className="min-h-11"
                onClick={() => setFilter(EMPTY_HISTORY_FILTER)}>Clear filters</Button>
            </div>
          </div>

          <div className="max-h-[45vh] overflow-y-auto space-y-2">
            {history.length === 0 && (
              <p className="text-sm text-muted-foreground">Nothing logged yet. Tick your first activity today.</p>
            )}
            {history.length > 0 && visibleHistory.length === 0 && (
              <p className="text-sm text-muted-foreground">No days match that search. Try fewer filters.</p>
            )}
            {visibleHistory.map((h) => (
              <button key={h.date}
                onClick={() => { setDate(h.date); setHistoryOpen(false); }}
                className="w-full rounded-xl border border-white/10 bg-white/[0.02] p-3 text-left hover:bg-white/[0.06] transition">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{formatDay(h.date)}</span>
                  <span className="text-[11px] text-muted-foreground">{h.date}</span>
                  <span className="ml-auto text-[11px] text-muted-foreground">
                    {h.doneIds.length}/{h.total || h.doneIds.length}
                  </span>
                  {h.complete && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
                </div>
                {h.doneIds.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {h.doneIds.slice(0, 4).map((id) => {
                      const t = book.tasks.find((x) => x.id === id);
                      return t ? (
                        <span key={id} className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] text-muted-foreground">
                          {t.label.length > 28 ? `${t.label.slice(0, 28)}…` : t.label}
                        </span>
                      ) : null;
                    })}
                    {h.doneIds.length > 4 && (
                      <span className="text-[10px] text-muted-foreground">+{h.doneIds.length - 4} more</span>
                    )}
                  </div>
                )}
                {h.note && <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{h.note}</p>}
              </button>
            ))}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" className="min-h-11" onClick={doExport}>
              <Download className="h-4 w-4 mr-1.5" /> Export backup
            </Button>
            <Button variant="outline" className="min-h-11" onClick={() => importRef.current?.click()}>
              <RotateCcw className="h-4 w-4 mr-1.5" /> Import backup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminders */}
      <Dialog open={remindOpen} onOpenChange={setRemindOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Daily reminders</DialogTitle>
            <DialogDescription>
              We'll nudge you once a day at the time you choose — no nagging, no streak guilt.
            </DialogDescription>
          </DialogHeader>

          {!userId ? (
            <p className="text-sm text-muted-foreground">
              Sign in to set reminders — they're sent from your account.
            </p>
          ) : (
            <div className="space-y-4">
              <label className="flex items-center gap-3 text-sm">
                <input type="checkbox" className="h-5 w-5 accent-primary" checked={reminder.enabled}
                  onChange={(e) => setReminder((r) => ({ ...r, enabled: e.target.checked }))} />
                Remind me about my scheduled activities
              </label>

              <div>
                <label htmlFor="vb-remind-hour" className="text-sm">Send at</label>
                <select id="vb-remind-hour" disabled={!reminder.enabled}
                  className="mt-1 min-h-11 w-full rounded-xl border border-white/10 bg-background px-3 text-sm disabled:opacity-50"
                  value={reminder.sendHour}
                  onChange={(e) => setReminder((r) => ({ ...r, sendHour: Number(e.target.value) }))}>
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>{formatHour(h)}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Your time zone: {reminder.timezone}
                  {reminder.timezone !== guessTimezone() && (
                    <Button variant="link" size="sm" className="h-auto p-0 ml-1 text-[11px]"
                      onClick={() => setReminder((r) => ({ ...r, timezone: guessTimezone() }))}>
                      use {guessTimezone()}
                    </Button>
                  )}
                </p>
              </div>

              <label className="flex items-center gap-3 text-sm">
                <input type="checkbox" className="h-5 w-5 accent-primary" disabled={!reminder.enabled}
                  checked={reminder.emailEnabled}
                  onChange={(e) => setReminder((r) => ({ ...r, emailEnabled: e.target.checked }))} />
                Email me
              </label>
              <label className="flex items-center gap-3 text-sm">
                <input type="checkbox" className="h-5 w-5 accent-primary" disabled={!reminder.enabled}
                  checked={reminder.pushEnabled}
                  onChange={(e) => setReminder((r) => ({ ...r, pushEnabled: e.target.checked }))} />
                Push notification on this device
                <span className="text-[11px] text-muted-foreground">(when the app is installed)</span>
              </label>
              <label className="flex items-center gap-3 text-sm">
                <input type="checkbox" className="h-5 w-5 accent-primary" disabled={!reminder.enabled}
                  checked={reminder.skipWhenNothingDue}
                  onChange={(e) => setReminder((r) => ({ ...r, skipWhenNothingDue: e.target.checked }))} />
                Stay quiet on days with nothing scheduled
              </label>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" className="min-h-11" onClick={() => setRemindOpen(false)}>Cancel</Button>
            <Button className="min-h-11" disabled={!userId || remindSaving}
              onClick={async () => {
                if (!userId) return;
                setRemindSaving(true);
                try {
                  await saveReminderSettings(userId, reminder);
                  toast.success(reminder.enabled
                    ? `Reminders on — daily at ${formatHour(reminder.sendHour)}.`
                    : "Reminders off.");
                  setRemindOpen(false);
                } catch (e) {
                  toast.error("Couldn't save your reminder settings. Try again.");
                  console.warn("reminder save failed", e);
                } finally {
                  setRemindSaving(false);
                }
              }}>
              {remindSaving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save reminders
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <input ref={importRef} type="file" accept="application/json,.json" className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) void doImport(file);
        }} />


      {/* Activity manager */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Your activities</DialogTitle>
            <DialogDescription>Add, rename, reschedule or retire what you're tracking.</DialogDescription>
          </DialogHeader>

          <div className="max-h-[45vh] overflow-y-auto space-y-2">
            {activeTasks.map((t) => (
              <div key={t.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                {editing === t.id ? (
                  <div className="space-y-3">
                    <Input defaultValue={t.label} aria-label="Activity name"
                      onBlur={(e) => setBook((b) => updateTask(b, t.id, { label: e.target.value }))} />
                    <ScheduleEditor value={t.schedule}
                      onChange={(s) => setBook((b) => updateTask(b, t.id, { schedule: s }))} />
                    <Button size="sm" className="min-h-11" onClick={() => setEditing(null)}>Done</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="min-w-0">
                      <div className="text-sm truncate">{t.label}</div>
                      <div className="text-[11px] text-muted-foreground">{describeSchedule(t.schedule)}</div>
                    </div>
                    <div className="ml-auto flex items-center gap-1">
                      <Button asChild variant="ghost" size="icon" className="min-h-11 min-w-11"
                        title={`Add "${t.label}" to Google Calendar — ${describeSchedule(t.schedule).toLowerCase()}`}>
                        <a
                          href={googleCalendarUrl({
                            title: t.label,
                            schedule: t.schedule,
                            hour: reminder.sendHour,
                          })}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Add ${t.label} to Google Calendar`}
                        >
                          <CalendarPlus className="h-4 w-4" />
                        </a>
                      </Button>
                      <Button variant="ghost" size="icon" className="min-h-11 min-w-11" aria-label={`Edit ${t.label}`}
                        onClick={() => setEditing(t.id)}>
                        <Pencil className="h-4 w-4" />
                      </Button>

                      <Button variant="ghost" size="icon" className="min-h-11 min-w-11" aria-label={`Retire ${t.label}`}
                        onClick={() => setBook((b) => archiveTask(b, t.id))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {retiredTasks.length > 0 && (
              <div className="pt-2">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Retired</div>
                {retiredTasks.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 rounded-xl border border-white/5 p-3">
                    <span className="text-sm text-muted-foreground truncate">{t.label}</span>
                    <Button variant="ghost" size="sm" className="ml-auto min-h-11"
                      onClick={() => setBook((b) => restoreTask(b, t.id))}>
                      <RotateCcw className="h-4 w-4 mr-1.5" /> Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-white/10 p-3 space-y-3">
            <div className="font-display text-sm">Add an activity</div>
            <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
              aria-label="New activity name" placeholder="e.g. 30 minutes of portfolio work" />
            <ScheduleEditor value={newSchedule} onChange={setNewSchedule} />
            <Button className="min-h-11" disabled={!newLabel.trim()}
              onClick={() => {
                setBook((b) => addTask(b, newLabel, newSchedule));
                setNewLabel("");
                setNewSchedule({ kind: "daily" });
                toast.success("Activity added.");
              }}>
              <Plus className="h-4 w-4 mr-1.5" /> Add activity
            </Button>
          </div>

          <p className="text-[11px] text-muted-foreground">
            Tap the calendar icon on any activity to add it to Google Calendar — it repeats on the
            same schedule you set here, and nothing is added until you confirm in Google.
          </p>

          <DialogFooter>
            <Button variant="outline" className="min-h-11" onClick={() => setManageOpen(false)}>Close</Button>
            <Button className="min-h-11" disabled={activitiesSaving} onClick={saveActivitiesNow}>
              {activitiesSaving
                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <Save className="h-4 w-4 mr-1.5" />}
              Save changes
            </Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>
    </Section>
  );
}


// =========================
// SECTION 11 — Future Probability
// =========================
function SectionProbability(_props: {
  identity: ReturnType<typeof deriveIdentity>;
  actions: ActionPlan[]; wheel: WheelArea[]; dreams: Dream[];
  streak: { count: number; last: string };
}) {
  // Layer 5 lives in the engine. This section renders what comes back —
  // value, confidence, components and the reason — and never computes a score.
  const { indicators, loading, coverage, saving, recompute } = useSkillsIntelligence();

  return (
    <Section eyebrow="Section 11" title="Future Probability"
      subtitle="Living indicators — not grades. They breathe as you build.">
      <FutureProbabilityPanel
        indicators={indicators}
        loading={loading}
        coverage={coverage}
        refreshing={saving}
        onRefresh={recompute}
        embedded
      />
      <div className="mt-6">
        <Button asChild variant="outline" className="min-h-[48px]">
          <Link to="/skills">Add evidence in Skills Intelligence™</Link>
        </Button>
      </div>
    </Section>
  );
}


// =========================
// SECTION 12 — North Star
// =========================
function SectionNorthStar({ purpose, streak, actions }: {
  purpose: PurposePack | null; streak: { count: number };
  actions: ActionPlan[];
}) {
  const topGoal = actions[0]?.dreamTitle ?? "Define your first dream";
  const nextStep = actions.flatMap(a => a.steps).find(s => !s.done)?.label ?? "Generate an action plan";

  return (
    <section className="relative overflow-hidden mt-12">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-primary/15" />
        <div className="absolute inset-0 opacity-30"
          style={{ background: "radial-gradient(circle at 50% 100%, #00665B, transparent 60%)" }} />
      </div>
      <div className="container mx-auto px-4 sm:px-6 py-20 sm:py-32 text-center">
        <div className="text-[11px] font-display uppercase tracking-[0.4em] text-primary/80 mb-4">Section 12 · My North Star</div>
        <h2 className="font-display text-4xl sm:text-6xl font-bold text-balance max-w-3xl mx-auto">
          The Future You're Building
        </h2>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto text-left">
          <GlassCard className="p-5">
            <div className="text-[10px] uppercase tracking-wider text-primary/80">Purpose</div>
            <div className="mt-2 font-display text-base">{purpose?.purpose ?? "Build your purpose in section 4."}</div>
          </GlassCard>
          <GlassCard className="p-5">
            <div className="text-[10px] uppercase tracking-wider text-primary/80">Mission</div>
            <div className="mt-2 font-display text-base">{purpose?.mission ?? "—"}</div>
          </GlassCard>
          <GlassCard className="p-5">
            <div className="text-[10px] uppercase tracking-wider text-primary/80">Top career goal</div>
            <div className="mt-2 font-display text-base">{topGoal}</div>
          </GlassCard>
          <GlassCard className="p-5">
            <div className="text-[10px] uppercase tracking-wider text-primary/80">Current streak</div>
            <div className="mt-2 font-display text-2xl flex items-center gap-2"><Flame className="h-5 w-5 text-orange-400" /> {streak.count} days</div>
          </GlassCard>
          <GlassCard className="p-5">
            <div className="text-[10px] uppercase tracking-wider text-primary/80">Next milestone</div>
            <div className="mt-2 font-display text-base">{nextStep}</div>
          </GlassCard>
          <GlassCard className="p-5">
            <div className="text-[10px] uppercase tracking-wider text-primary/80">YOMA encouragement</div>
            <div className="mt-2 font-display text-base">You're closer than you think. Take the next step today.</div>
          </GlassCard>
        </div>

        <div className="mt-16 font-display text-2xl sm:text-3xl text-balance">
          Small actions. Every day. <span className="text-primary">Extraordinary future.</span>
        </div>
      </div>
    </section>
  );
}
